import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    CircularProgress,
    Alert,
    Chip,
    Breadcrumbs,
    Link,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    ListItemIcon,
    Divider,
    Autocomplete,
    TextField,
    Checkbox,
    Tabs,
    Tab
} from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import ReactECharts from 'echarts-for-react';
import {
    getMetricsTrending,
    getAvailableDimensionsForMetrics,
    getChildrenComparison,
    MetricsTrendingResponse,
    HierarchyChildInfo,
    ChildrenComparisonResponse
} from '../../services/api';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import FilterListIcon from '@mui/icons-material/FilterList';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import FunctionsIcon from '@mui/icons-material/Functions';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import FolderIcon from '@mui/icons-material/Folder';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { formatDisplayDate } from '../../utils/dateUtils';
import {
    formatMetricLevelInstanceLabel,
    metricLevelSeriesKey,
    PROJECT_METRIC_LEVEL,
    AGGREGATE_METRIC_LEVEL,
    seriesInMetricLevelScope,
    isProjectLevelMetric,
    isAggregateLevelMetric,
    sortMetricLevelTypes,
} from '../../utils/metricLevelUtils';
import {
    getThresholdBoundColors,
    TARGET_LINE_COLOR,
} from '../MetricsChart/MetricsChart';
import { getReviewEntityMetricLevelConfig, MetricLevelConfig } from '../../services/reviewEntityMetricLevelService';
import MetricLevelScopeControls from './MetricLevelScopeControls';

export type DimensionFetchState =
    | { status: 'loading' }
    | { status: 'ok'; data: MetricsTrendingResponse }
    | { status: 'error'; error: string };

export interface BreadcrumbItem {
    id: number;
    name: string;
}

export interface ReviewEntityMetricsTrendingProps {
    /** When unset, optional empty state is shown (reports). Dashboard always passes an id. */
    reviewEntityId?: number | '' | null;
    /** Passed to getMetricsTrending (default true). */
    includeAggregate?: boolean;
    /** When false, breadcrumbs and drill-down are hidden (e.g. dashboard). */
    showEntityNavigation?: boolean;
    navigationPath?: BreadcrumbItem[];
    onDrillDown?: (child: HierarchyChildInfo) => void;
    onBreadcrumbClick?: (index: number) => void;
    /** When true and reviewEntityId is missing, show the select-entity placeholder. */
    showSelectEntityPrompt?: boolean;
}

const ReviewEntityMetricsTrending: React.FC<ReviewEntityMetricsTrendingProps> = ({
    reviewEntityId,
    includeAggregate = true,
    showEntityNavigation = true,
    navigationPath = [],
    onDrillDown,
    onBreadcrumbClick,
    showSelectEntityPrompt = true,
}) => {
    const [applicableDimensions, setApplicableDimensions] = useState<string[]>([]);
    const [dimensionResults, setDimensionResults] = useState<Record<string, DimensionFetchState>>({});
    const [activeDimensionName, setActiveDimensionName] = useState<string>('');
    const [loadingApplicableDimensions, setLoadingApplicableDimensions] = useState(false);

    // State for data (derived from active dimension tab — kept as trendingData for minimal downstream changes)
    const [selectedMetrics, setSelectedMetrics] = useState<string[]>([]);
    
    // State for view mode (aggregate vs compare)
    const [viewMode, setViewMode] = useState<'aggregate' | 'compare'>('aggregate');
    const [comparisonData, setComparisonData] = useState<ChildrenComparisonResponse | null>(null);
    const [selectedCompareMetric, setSelectedCompareMetric] = useState<string>('');
    const [loadingComparison, setLoadingComparison] = useState(false);
    
    // State for chart type (line vs bar)
    const [chartType, setChartType] = useState<'line' | 'bar'>('line');
    
    // State for active metric tab
    const [activeMetricTab, setActiveMetricTab] = useState<number>(0);
    
    // State for filtering metric_identifier_names per metric
    const [selectedIdentifiers, setSelectedIdentifiers] = useState<{ [metricName: string]: string[] }>({});

    const [metricLevelConfig, setMetricLevelConfig] = useState<MetricLevelConfig | null>(null);
    const [selectedLevelType, setSelectedLevelType] = useState<string>(PROJECT_METRIC_LEVEL);
    const [selectedInstanceId, setSelectedInstanceId] = useState<string | null>(null);
    
    const [error, setError] = useState<string>('');

    const sortedDimensionNames = useMemo(() => {
        const dims = applicableDimensions;
        const tier = (d: string): number => {
            const dr = dimensionResults[d];
            if (!dr) return 1;
            if (dr.status === 'loading') return 1;
            if (dr.status === 'error') return 2;
            const n = dr.data?.metrics_by_name ? Object.keys(dr.data.metrics_by_name).length : 0;
            return n > 0 ? 0 : 1;
        };
        return [...dims].sort((a, b) => tier(a) - tier(b) || a.localeCompare(b));
    }, [applicableDimensions, dimensionResults]);

    const trendingData = useMemo((): MetricsTrendingResponse | null => {
        if (!activeDimensionName) return null;
        const dr = dimensionResults[activeDimensionName];
        if (dr?.status === 'ok' && dr.data) return dr.data;
        return null;
    }, [activeDimensionName, dimensionResults]);

    useEffect(() => {
        if (sortedDimensionNames.length === 0) {
            if (activeDimensionName) setActiveDimensionName('');
            return;
        }
        if (!activeDimensionName || !sortedDimensionNames.includes(activeDimensionName)) {
            setActiveDimensionName(sortedDimensionNames[0]);
        }
    }, [sortedDimensionNames, activeDimensionName]);

    useEffect(() => {
        if (!reviewEntityId) {
            setApplicableDimensions([]);
            setDimensionResults({});
            setComparisonData(null);
            setSelectedCompareMetric('');
            setActiveDimensionName('');
            setMetricLevelConfig(null);
            setSelectedLevelType(PROJECT_METRIC_LEVEL);
            setSelectedInstanceId(null);
            return;
        }

        let cancelled = false;
        const entityId = reviewEntityId as number;
        const loadDimensionsAndMetrics = async () => {
            setLoadingApplicableDimensions(true);
            setError('');
            try {
                const dimRes = await getAvailableDimensionsForMetrics(entityId);
                if (cancelled) return;
                const dims = dimRes.data || [];
                setApplicableDimensions(dims);

                const loadingMap: Record<string, DimensionFetchState> = {};
                dims.forEach((d) => {
                    loadingMap[d] = { status: 'loading' };
                });
                setDimensionResults(loadingMap);

                const next: Record<string, DimensionFetchState> = {};
                await Promise.all(
                    dims.map(async (dim) => {
                        try {
                            const response = await getMetricsTrending(entityId, dim, includeAggregate);
                            if (cancelled) return;
                            next[dim] = { status: 'ok', data: response.data };
                        } catch (err: any) {
                            if (cancelled) return;
                            next[dim] = {
                                status: 'error',
                                error:
                                    err.response?.data?.detail ||
                                    err.message ||
                                    'Failed to load trending data',
                            };
                        }
                    }),
                );
                if (!cancelled) {
                    setDimensionResults(next);
                }
            } catch (err: any) {
                if (!cancelled) {
                    console.error('Error loading dimensions for entity:', err);
                    setApplicableDimensions([]);
                    setDimensionResults({});
                    setError(err.response?.data?.detail || err.message || 'Failed to load dimensions');
                }
            } finally {
                if (!cancelled) {
                    setLoadingApplicableDimensions(false);
                }
            }
        };

        loadDimensionsAndMetrics();
        setComparisonData(null);
        setSelectedCompareMetric('');
        setSelectedLevelType(PROJECT_METRIC_LEVEL);
        setSelectedInstanceId(null);
        setSelectedIdentifiers({});
        setMetricLevelConfig(null);

        const loadLevelConfig = async () => {
            try {
                const cfg = await getReviewEntityMetricLevelConfig(entityId);
                if (!cancelled) {
                    setMetricLevelConfig(cfg);
                }
            } catch {
                if (!cancelled) {
                    setMetricLevelConfig(null);
                }
            }
        };
        loadLevelConfig();

        return () => {
            cancelled = true;
        };
    }, [reviewEntityId, includeAggregate]);

    // Reset selected metrics when data changes
    useEffect(() => {
        if (trendingData && trendingData.metrics_by_name) {
            const metricNames = Object.keys(trendingData.metrics_by_name);
            // Select first 5 metrics by default
            setSelectedMetrics(metricNames.slice(0, 5));
            setActiveMetricTab(0);
        } else {
            setSelectedMetrics([]);
            setActiveMetricTab(0);
        }
    }, [trendingData]);

    useEffect(() => {
        if (trendingData?.is_aggregate) {
            setSelectedLevelType((prev) =>
                prev === PROJECT_METRIC_LEVEL ? AGGREGATE_METRIC_LEVEL : prev
            );
        }
    }, [trendingData?.is_aggregate]);

    // Reset active tab when selected metrics change
    useEffect(() => {
        if (activeMetricTab >= selectedMetrics.length) {
            setActiveMetricTab(Math.max(0, selectedMetrics.length - 1));
        }
    }, [selectedMetrics, activeMetricTab]);

    const fetchComparisonData = useCallback(async () => {
        if (!reviewEntityId) return;

        try {
            setLoadingComparison(true);
            const response = await getChildrenComparison(
                reviewEntityId as number,
                activeDimensionName || undefined,
                undefined
            );
            setComparisonData(response.data);
            setSelectedCompareMetric((prev) => {
                if (!prev && response.data.available_metrics.length > 0) {
                    return response.data.available_metrics[0];
                }
                return prev;
            });
        } catch (err: any) {
            console.error('Error fetching comparison data:', err);
            setError(err.response?.data?.detail || err.message || 'Failed to load comparison data');
        } finally {
            setLoadingComparison(false);
        }
    }, [reviewEntityId, activeDimensionName]);

    const handleDrillDown = (child: HierarchyChildInfo) => {
        onDrillDown?.(child);
    };

    const handleBreadcrumbClick = (index: number) => {
        onBreadcrumbClick?.(index);
    };

    // Handle view mode change
    const handleViewModeChange = (
        _event: React.MouseEvent<HTMLElement>,
        newMode: 'aggregate' | 'compare' | null
    ) => {
        if (newMode !== null) {
            setViewMode(newMode);
        }
    };

    // Get available metric names from data
    const availableMetrics = useMemo(() => {
        if (!trendingData || !trendingData.metrics_by_name) return [];
        return Object.keys(trendingData.metrics_by_name);
    }, [trendingData]);

    const availableLevelTypes = useMemo(() => {
        const types = new Set<string>();
        (metricLevelConfig?.metric_level_names || []).forEach((n) => {
            if (n) types.add(n.toUpperCase());
        });
        types.add(PROJECT_METRIC_LEVEL);
        if (trendingData?.metrics_by_name) {
            Object.values(trendingData.metrics_by_name).forEach((metricData) => {
                (metricData.data || []).forEach((d) => {
                    if (isProjectLevelMetric(d)) {
                        types.add(PROJECT_METRIC_LEVEL);
                    } else if (isAggregateLevelMetric(d)) {
                        types.add(AGGREGATE_METRIC_LEVEL);
                    } else if (d.metric_identifier_type) {
                        types.add(d.metric_identifier_type.toUpperCase());
                    }
                });
            });
        }
        return sortMetricLevelTypes(Array.from(types));
    }, [metricLevelConfig, trendingData]);

    const levelScope = useMemo(
        () => ({
            levelType: selectedLevelType || PROJECT_METRIC_LEVEL,
            instanceId: selectedInstanceId,
        }),
        [selectedLevelType, selectedInstanceId]
    );

    const pointInScope = useCallback(
        (point: { metric_identifier_type?: string | null; metric_identifier_name?: string | null }) =>
            seriesInMetricLevelScope(point, levelScope, metricLevelConfig?.instances || []),
        [levelScope, metricLevelConfig]
    );

    const handleLevelTypeChange = (levelType: string) => {
        setSelectedLevelType(levelType);
        setSelectedInstanceId(null);
        setSelectedIdentifiers({});
    };

    const handleInstanceChange = (instanceId: string | null) => {
        setSelectedInstanceId(instanceId);
        setSelectedIdentifiers({});
    };

    const handleLevelBreadcrumbNavigate = (levelType: string, instanceId: string | null) => {
        setSelectedLevelType(levelType || PROJECT_METRIC_LEVEL);
        setSelectedInstanceId(instanceId);
        setSelectedIdentifiers({});
    };

    const activeDimensionState: DimensionFetchState | undefined = activeDimensionName
        ? dimensionResults[activeDimensionName]
        : undefined;

    // Auto-select first metric when switching to compare mode or when comparison data loads
    useEffect(() => {
        if (viewMode === 'compare' && comparisonData) {
            // Use metrics from comparison_by_metric (these are guaranteed to have chart data)
            const metricsWithData = Object.keys(comparisonData.comparison_by_metric);
            if (metricsWithData.length > 0) {
                // Only update if current selection is not in the available metrics
                if (!selectedCompareMetric || !metricsWithData.includes(selectedCompareMetric)) {
                    setSelectedCompareMetric(metricsWithData[0]);
                }
            }
        }
    }, [viewMode, comparisonData, selectedCompareMetric]);

    useEffect(() => {
        if (viewMode === 'compare' && reviewEntityId && trendingData?.is_aggregate) {
            fetchComparisonData();
        }
    }, [viewMode, reviewEntityId, trendingData?.is_aggregate, fetchComparisonData]);

    // Helper function to format values with uniform precision.
    const formatWithSmartPrecision = (value: number, prefix: string = '', suffix: string = ''): string => {
        if (value === null || value === undefined) return '';
        
        const absValue = Math.abs(value);
        
        // Large values: use K/M notation
        if (absValue >= 1000000) {
            return prefix + (value / 1000000).toFixed(2) + 'M' + suffix;
        } else if (absValue >= 1000) {
            return prefix + (value / 1000).toFixed(2) + 'K' + suffix;
        }
        
        // Keep all non-abbreviated values to 2 decimals for consistency.
        return prefix + value.toFixed(2) + suffix;
    };

    // Helper function to format Y-axis values based on value_type
    const formatYAxisValue = (value: number, valueType: string | null | undefined): string => {
        if (value === null || value === undefined) return '';
        
        switch (valueType?.toLowerCase()) {
            case 'percentage':
                // For percentages stored as decimals (e.g., 0.05 = 5%)
                if (Math.abs(value) <= 1 && Math.abs(value) > 0) {
                    const percentValue = value * 100;
                    return formatWithSmartPrecision(percentValue, '', '%');
                }
                return formatWithSmartPrecision(value, '', '%');
            case 'ratio':
                return formatWithSmartPrecision(value);
            case 'amount':
                return formatWithSmartPrecision(value, '$');
            case 'uom':
            default:
                return formatWithSmartPrecision(value);
        }
    };

    // Get Y-axis label based on value_type
    const getYAxisLabel = (valueType: string | null | undefined): string => {
        switch (valueType?.toLowerCase()) {
            case 'percentage':
                return 'Percentage (%)';
            case 'ratio':
                return 'Ratio';
            case 'amount':
                return 'Amount ($)';
            case 'uom':
                return 'Units';
            default:
                return 'Value';
        }
    };

    // Handle chart type change
    const handleChartTypeChange = (event: React.MouseEvent<HTMLElement>, newChartType: 'line' | 'bar' | null) => {
        if (newChartType !== null) {
            setChartType(newChartType);
        }
    };

    // Generate comparison chart options
    const getComparisonChartOption = () => {
        if (!comparisonData || !selectedCompareMetric) return null;
        
        const metricData = comparisonData.comparison_by_metric[selectedCompareMetric];
        
        // If no data for selected metric, show empty state
        if (!metricData || !metricData.series || metricData.series.length === 0) {
            console.log('No metric data found for:', selectedCompareMetric);
            console.log('Available metrics in comparison_by_metric:', Object.keys(comparisonData.comparison_by_metric));
            return null;
        }

        // Get value_type and threshold bounds from metric data
        const valueType = (metricData as any).value_type;
        const unitOfMeasure = (metricData as any).unit_of_measure;
        const metricsType = (metricData as any)?.metrics_type;
        const targetValue = (metricData as any)?.target_value;
        // Org-level thresholds
        const orgLowerBound = (metricData as any)?.org_lower_bound;
        const orgUpperBound = (metricData as any)?.org_upper_bound;
        // Entity-level threshold overrides
        const entityLowerBound = (metricData as any)?.entity_lower_bound;
        const entityUpperBound = (metricData as any)?.entity_upper_bound;
        const isThresholdOverridden = (metricData as any)?.is_threshold_overridden;
        const boundColors = getThresholdBoundColors(metricsType);
        const softBoundColors = getThresholdBoundColors(metricsType, true);
        
        // Calculate min/max for smart Y-axis scaling across all series
        const allValues: number[] = [];
        metricData.series.forEach(s => {
            s.data.forEach(v => {
                if (v !== null && v !== undefined) allValues.push(v);
            });
        });
        let compMinValue = allValues.length > 0 ? Math.min(...allValues) : 0;
        let compMaxValue = allValues.length > 0 ? Math.max(...allValues) : 0;
        
        // Extend range to include all threshold bounds so they are visible
        const compAllBounds: number[] = [];
        if (orgLowerBound !== null && orgLowerBound !== undefined) compAllBounds.push(orgLowerBound);
        if (orgUpperBound !== null && orgUpperBound !== undefined) compAllBounds.push(orgUpperBound);
        if (entityLowerBound !== null && entityLowerBound !== undefined) compAllBounds.push(entityLowerBound);
        if (entityUpperBound !== null && entityUpperBound !== undefined) compAllBounds.push(entityUpperBound);
        if (targetValue !== null && targetValue !== undefined) compAllBounds.push(targetValue);
        
        if (compAllBounds.length > 0) {
            compMinValue = Math.min(compMinValue, ...compAllBounds);
            compMaxValue = Math.max(compMaxValue, ...compAllBounds);
        }
        
        const compValueRange = compMaxValue - compMinValue;
        
        // Determine appropriate Y-axis interval based on data range
        const getCompSmartInterval = (range: number, min: number, max: number): number | undefined => {
            if (range === 0) return undefined;
            const absMax = Math.max(Math.abs(min), Math.abs(max));
            if (absMax < 1) return range / 5;
            return undefined;
        };
        const compYAxisInterval = getCompSmartInterval(compValueRange, compMinValue, compMaxValue);
        
        // Build mark lines for thresholds - show both org-level and entity-level
        const compMarkLine: any = { 
            data: [],
            silent: true,
            symbol: 'none'
        };
        
        // Org-level thresholds (shown as dashed lines)
        if (orgLowerBound !== null && orgLowerBound !== undefined) {
            compMarkLine.data.push({
                yAxis: orgLowerBound,
                name: 'Org Lower',
                lineStyle: { color: softBoundColors.lower, type: 'dashed', width: 1.5 },
                label: { 
                    formatter: `Org Lower: ${formatYAxisValue(orgLowerBound, valueType)}`, 
                    position: 'insideEndTop',
                    fontSize: 10,
                    color: softBoundColors.lower
                }
            });
        }
        if (orgUpperBound !== null && orgUpperBound !== undefined) {
            compMarkLine.data.push({
                yAxis: orgUpperBound,
                name: 'Org Upper',
                lineStyle: { color: softBoundColors.upper, type: 'dashed', width: 1.5 },
                label: { 
                    formatter: `Org Upper: ${formatYAxisValue(orgUpperBound, valueType)}`, 
                    position: 'insideEndTop',
                    fontSize: 10,
                    color: softBoundColors.upper
                }
            });
        }
        
        // Entity-level threshold overrides (shown as solid lines)
        if (isThresholdOverridden) {
            if (entityLowerBound !== null && entityLowerBound !== undefined && entityLowerBound !== orgLowerBound) {
                compMarkLine.data.push({
                    yAxis: entityLowerBound,
                    name: 'Entity Lower',
                    lineStyle: { color: boundColors.lower, type: 'solid', width: 2 },
                    label: { 
                        formatter: `Entity Lower: ${formatYAxisValue(entityLowerBound, valueType)}`, 
                        position: 'insideEndBottom',
                        fontSize: 10,
                        color: boundColors.lower,
                        fontWeight: 'bold'
                    }
                });
            }
            if (entityUpperBound !== null && entityUpperBound !== undefined && entityUpperBound !== orgUpperBound) {
                compMarkLine.data.push({
                    yAxis: entityUpperBound,
                    name: 'Entity Upper',
                    lineStyle: { color: boundColors.upper, type: 'solid', width: 2 },
                    label: { 
                        formatter: `Entity Upper: ${formatYAxisValue(entityUpperBound, valueType)}`, 
                        position: 'insideEndBottom',
                        fontSize: 10,
                        color: boundColors.upper,
                        fontWeight: 'bold'
                    }
                });
            }
        }

        // Target value — prominent yellow line
        if (targetValue !== null && targetValue !== undefined) {
            compMarkLine.data.push({
                yAxis: targetValue,
                name: 'Target',
                lineStyle: { color: TARGET_LINE_COLOR, type: 'solid', width: 3 },
                label: {
                    formatter: `Target: ${formatYAxisValue(targetValue, valueType)}`,
                    position: 'insideStartTop',
                    fontSize: 12,
                    color: TARGET_LINE_COLOR,
                    fontWeight: 'bold'
                }
            });
        }
        
        const series = metricData.series.map((s, index) => ({
            name: s.entity_name,
            type: chartType,
            data: s.data,
            smooth: chartType === 'line',
            symbol: chartType === 'line' ? 'circle' : undefined,
            symbolSize: chartType === 'line' ? 8 : undefined,
            lineStyle: chartType === 'line' ? { width: 2 } : undefined,
            emphasis: { focus: 'series' },
            // Add mark lines only to the first series to avoid duplicates
            markLine: index === 0 && compMarkLine.data.length > 0 ? compMarkLine : undefined
        }));

        return {
            title: {
                text: `Compare: ${selectedCompareMetric}`,
                subtext: `Comparing ${comparisonData.children_count} child entities of ${comparisonData.parent_entity_name}`,
                left: 'center',
                textStyle: { fontSize: 18, fontWeight: 'bold' }
            },
            tooltip: {
                trigger: 'axis',
                axisPointer: { type: 'cross' },
                formatter: (params: any) => {
                    if (!Array.isArray(params)) return '';
                    let result = `<strong>${params[0]?.axisValue}</strong><br/>`;
                    params.forEach((param: any) => {
                        const formattedValue = formatYAxisValue(param.value, valueType);
                        result += `${param.marker} ${param.seriesName}: ${formattedValue}<br/>`;
                    });
                    if (unitOfMeasure) {
                        result += `<span style="color:#666;font-size:11px;">Unit: ${unitOfMeasure}</span>`;
                    }
                    return result;
                }
            },
            legend: {
                data: metricData.series.map(s => s.entity_name),
                bottom: 0,
                type: 'scroll',
                textStyle: { fontSize: 11 }
            },
            grid: {
                left: '3%',
                right: '4%',
                bottom: '15%',
                top: '15%',
                containLabel: true
            },
            xAxis: {
                type: 'category',
                data: metricData.dates.map((d) => formatDisplayDate(d)),
                axisLabel: { rotate: 45, fontSize: 10 },
                name: 'Date',
                nameLocation: 'middle',
                nameGap: 50
            },
            yAxis: {
                type: 'value',
                name: getYAxisLabel(valueType),
                nameLocation: 'middle',
                nameGap: 70,
                interval: compYAxisInterval,
                min: compValueRange > 0 ? compMinValue - compValueRange * 0.1 : undefined,
                max: compValueRange > 0 ? compMaxValue + compValueRange * 0.1 : undefined,
                axisLabel: {
                    formatter: (value: number) => formatYAxisValue(value, valueType)
                }
            },
            series: series,
            dataZoom: [
                { type: 'inside', start: 0, end: 100 },
                { start: 0, end: 100 }
            ]
        };
    };

    const comparisonChartOption = getComparisonChartOption();

    // Generate chart options for a single metric (used in tabbed view)
    const getSingleMetricChartOption = (metricName: string) => {
        if (!trendingData || !trendingData.metrics_by_name) {
            return null;
        }

        const metricData = trendingData.metrics_by_name[metricName];
        if (!metricData || !metricData.data) {
            return null;
        }

        // Get dates and values for this metric
        const sortedData = [...metricData.data].sort((a, b) => 
            (a.date || '').localeCompare(b.date || '')
        );
        const scopedData = sortedData.filter(pointInScope);
        if (scopedData.length === 0) {
            return null;
        }
        
        // Group data by metric level (type + instance) to create separate series
        const dataByIdentifier: { [key: string]: { date: string; value: number | null }[] } = {};
        const identifierLabels: { [key: string]: string } = {};
        const allDates = new Set<string>();
        
        scopedData.forEach(d => {
            const key = metricLevelSeriesKey(d.metric_identifier_type, d.metric_identifier_name);
            if (!dataByIdentifier[key]) {
                dataByIdentifier[key] = [];
                identifierLabels[key] = formatMetricLevelInstanceLabel(
                    d.metric_identifier_type,
                    d.metric_identifier_name
                );
            }
            dataByIdentifier[key].push({ date: d.date, value: d.value });
            if (d.date) allDates.add(d.date);
        });
        
        // Get unique sorted dates for x-axis
        const dates = Array.from(allDates).sort();
        const allIdentifierNames = Object.keys(dataByIdentifier).sort((a, b) =>
            (identifierLabels[a] || a).localeCompare(identifierLabels[b] || b)
        );
        const hasMultipleIdentifiers = allIdentifierNames.length > 1;
        
        // Filter identifiers based on user selection (if any selected, otherwise show all)
        const selectedForMetric = selectedIdentifiers[metricName];
        const filteredIdentifierNames = (selectedForMetric && selectedForMetric.length > 0)
            ? allIdentifierNames.filter(name => selectedForMetric.includes(name))
            : allIdentifierNames;
        
        // Color palette for multiple series
        const seriesColors = [
            '#1976d2', '#e91e63', '#4caf50', '#ff9800', '#9c27b0', 
            '#00bcd4', '#795548', '#607d8b', '#f44336', '#3f51b5'
        ];

        // Get value_type and threshold bounds for this metric
        const valueType = (metricData as any)?.value_type;
        const unitOfMeasure = (metricData as any)?.unit_of_measure;
        const metricsType = (metricData as any)?.metrics_type;
        const targetValue = (metricData as any)?.target_value;
        // Org-level thresholds
        const orgLowerBound = (metricData as any)?.org_lower_bound;
        const orgUpperBound = (metricData as any)?.org_upper_bound;
        // Entity-level threshold overrides
        const entityLowerBound = (metricData as any)?.entity_lower_bound;
        const entityUpperBound = (metricData as any)?.entity_upper_bound;
        const isThresholdOverridden = (metricData as any)?.is_threshold_overridden;
        const boundColors = getThresholdBoundColors(metricsType);
        const softBoundColors = getThresholdBoundColors(metricsType, true);

        // Calculate min/max for smart Y-axis scaling - include threshold bounds (only for filtered identifiers)
        const allValues: number[] = [];
        filteredIdentifierNames.forEach(identifierName => {
            const dataPoints = dataByIdentifier[identifierName] || [];
            dataPoints.forEach(d => {
                if (d.value !== null && d.value !== undefined) allValues.push(d.value);
            });
        });
        let minValue = allValues.length > 0 ? Math.min(...allValues) : 0;
        let maxValue = allValues.length > 0 ? Math.max(...allValues) : 0;
        
        // Extend range to include all threshold bounds so they are visible
        const allBounds: number[] = [];
        if (orgLowerBound !== null && orgLowerBound !== undefined) allBounds.push(orgLowerBound);
        if (orgUpperBound !== null && orgUpperBound !== undefined) allBounds.push(orgUpperBound);
        if (entityLowerBound !== null && entityLowerBound !== undefined) allBounds.push(entityLowerBound);
        if (entityUpperBound !== null && entityUpperBound !== undefined) allBounds.push(entityUpperBound);
        if (targetValue !== null && targetValue !== undefined) allBounds.push(targetValue);
        
        if (allBounds.length > 0) {
            minValue = Math.min(minValue, ...allBounds);
            maxValue = Math.max(maxValue, ...allBounds);
        }
        
        const valueRange = maxValue - minValue;
        
        // Determine appropriate Y-axis interval based on data range
        const getSmartInterval = (range: number, min: number, max: number): number | undefined => {
            if (range === 0) return undefined; // Let ECharts decide
            const absMax = Math.max(Math.abs(min), Math.abs(max));
            if (absMax < 0.001) return range / 5;
            if (absMax < 0.01) return range / 5;
            if (absMax < 0.1) return range / 5;
            if (absMax < 1) return range / 5;
            return undefined; // Let ECharts decide for larger values
        };
        
        const yAxisInterval = getSmartInterval(valueRange, minValue, maxValue);

        const isAggregate = trendingData.is_aggregate;
        const aggregateLabel = isAggregate ? ' (Aggregated)' : '';
        const subtextParts = [];
        if (metricData.review_dimension) subtextParts.push(`Dimension: ${metricData.review_dimension}`);
        if (valueType) subtextParts.push(`Type: ${valueType}`);
        if (isAggregate && trendingData.hierarchy_info) {
            subtextParts.push(`Avg of ${trendingData.hierarchy_info.leaf_count} entities`);
        }
        if (isThresholdOverridden) {
            subtextParts.push('⚠️ Entity Override Active');
        }

        // Build mark lines for thresholds - show both org-level and entity-level
        const markLine: any = { 
            data: [],
            silent: true,
            symbol: 'none'
        };
        
        // Org-level thresholds (shown as dashed lines)
        if (orgLowerBound !== null && orgLowerBound !== undefined) {
            markLine.data.push({
                yAxis: orgLowerBound,
                name: 'Org Lower',
                lineStyle: { color: softBoundColors.lower, type: 'dashed', width: 1.5 },
                label: { 
                    formatter: `Org Lower: ${formatYAxisValue(orgLowerBound, valueType)}`, 
                    position: 'insideEndTop',
                    fontSize: 10,
                    color: softBoundColors.lower
                }
            });
        }
        if (orgUpperBound !== null && orgUpperBound !== undefined) {
            markLine.data.push({
                yAxis: orgUpperBound,
                name: 'Org Upper',
                lineStyle: { color: softBoundColors.upper, type: 'dashed', width: 1.5 },
                label: { 
                    formatter: `Org Upper: ${formatYAxisValue(orgUpperBound, valueType)}`, 
                    position: 'insideEndTop',
                    fontSize: 10,
                    color: softBoundColors.upper
                }
            });
        }
        
        // Entity-level threshold overrides (shown as solid lines, only if different from org)
        if (isThresholdOverridden) {
            if (entityLowerBound !== null && entityLowerBound !== undefined && entityLowerBound !== orgLowerBound) {
                markLine.data.push({
                    yAxis: entityLowerBound,
                    name: 'Entity Lower',
                    lineStyle: { color: boundColors.lower, type: 'solid', width: 2 },
                    label: { 
                        formatter: `Entity Lower: ${formatYAxisValue(entityLowerBound, valueType)}`, 
                        position: 'insideEndBottom',
                        fontSize: 10,
                        color: boundColors.lower,
                        fontWeight: 'bold'
                    }
                });
            }
            if (entityUpperBound !== null && entityUpperBound !== undefined && entityUpperBound !== orgUpperBound) {
                markLine.data.push({
                    yAxis: entityUpperBound,
                    name: 'Entity Upper',
                    lineStyle: { color: boundColors.upper, type: 'solid', width: 2 },
                    label: { 
                        formatter: `Entity Upper: ${formatYAxisValue(entityUpperBound, valueType)}`, 
                        position: 'insideEndBottom',
                        fontSize: 10,
                        color: boundColors.upper,
                        fontWeight: 'bold'
                    }
                });
            }
        }

        // Target value — prominent yellow line
        if (targetValue !== null && targetValue !== undefined) {
            markLine.data.push({
                yAxis: targetValue,
                name: 'Target',
                lineStyle: { color: TARGET_LINE_COLOR, type: 'solid', width: 3 },
                label: {
                    formatter: `Target: ${formatYAxisValue(targetValue, valueType)}`,
                    position: 'insideStartTop',
                    fontSize: 12,
                    color: TARGET_LINE_COLOR,
                    fontWeight: 'bold'
                }
            });
        }

        return {
            title: {
                text: metricName + aggregateLabel,
                subtext: subtextParts.length > 0 ? subtextParts.join(' | ') : '',
                left: 'center',
                textStyle: {
                    fontSize: 16,
                    fontWeight: 'bold'
                }
            },
            tooltip: {
                trigger: 'axis',
                axisPointer: { type: 'cross' },
                formatter: (params: any) => {
                    if (!Array.isArray(params) || params.length === 0) return '';
                    let result = `<strong>${params[0]?.axisValue}</strong><br/>`;
                    params.forEach((param: any) => {
                        const formattedValue = formatYAxisValue(param.value, valueType);
                        if (param.value !== null && param.value !== undefined && param.value !== '-') {
                            result += `${param.marker} ${param.seriesName}: ${formattedValue}<br/>`;
                        }
                    });
                    if (unitOfMeasure) {
                        result += `<span style="color:#666;font-size:11px;">Unit: ${unitOfMeasure}</span>`;
                    }
                    return result;
                }
            },
            legend: hasMultipleIdentifiers ? {
                data: filteredIdentifierNames,
                bottom: 0,
                type: 'scroll',
                textStyle: { fontSize: 11 }
            } : undefined,
            grid: {
                left: '3%',
                right: '4%',
                bottom: hasMultipleIdentifiers ? '15%' : '10%',
                top: '18%',
                containLabel: true
            },
            xAxis: {
                type: 'category',
                data: dates.map((d) => formatDisplayDate(d)),
                axisLabel: { rotate: 45, fontSize: 10 },
                name: 'Date',
                nameLocation: 'middle',
                nameGap: 50
            },
            yAxis: {
                type: 'value',
                name: getYAxisLabel(valueType),
                nameLocation: 'middle',
                nameGap: 70,
                interval: yAxisInterval,
                min: valueRange > 0 ? minValue - valueRange * 0.1 : undefined,
                max: valueRange > 0 ? maxValue + valueRange * 0.1 : undefined,
                axisLabel: {
                    formatter: (value: number) => formatYAxisValue(value, valueType)
                }
            },
            series: filteredIdentifierNames.map((identifierName, index) => {
                // Build data array aligned with dates - use null for missing dates
                const identifierData = dataByIdentifier[identifierName];
                const dateValueMap = new Map(identifierData.map(d => [d.date, d.value]));
                const seriesData = dates.map(date => dateValueMap.get(date) ?? null);
                
                // Use consistent color based on position in allIdentifierNames (not filtered)
                const colorIndex = allIdentifierNames.indexOf(identifierName);
                
                return {
                    name: identifierLabels[identifierName] || identifierName,
                    type: chartType,
                    data: seriesData,
                    smooth: chartType === 'line',
                    symbol: chartType === 'line' ? 'circle' : undefined,
                    symbolSize: chartType === 'line' ? 8 : undefined,
                    lineStyle: chartType === 'line' ? { width: hasMultipleIdentifiers ? 2 : 3 } : undefined,
                    itemStyle: { color: seriesColors[colorIndex % seriesColors.length] },
                    areaStyle: chartType === 'line' && !hasMultipleIdentifiers ? { opacity: 0.1 } : undefined,
                    markLine: index === 0 && markLine.data.length > 0 ? markLine : undefined,
                    emphasis: { focus: 'series' },
                    connectNulls: false
                };
            }),
            dataZoom: [
                { type: 'inside', start: 0, end: 100 },
                { start: 0, end: 100 }
            ]
        };
    };

    // Handle metric tab change
    const handleMetricTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setActiveMetricTab(newValue);
    };

    // Get all metric-level series keys for a specific metric
    const getIdentifierNamesForMetric = (metricName: string): string[] => {
        if (!trendingData || !trendingData.metrics_by_name) return [];
        const metricData = trendingData.metrics_by_name[metricName];
        if (!metricData || !metricData.data) return [];
        
        const identifiers = new Map<string, string>();
        metricData.data.forEach((d: any) => {
            if (!pointInScope(d)) return;
            const key = metricLevelSeriesKey(d.metric_identifier_type, d.metric_identifier_name);
            if (!identifiers.has(key)) {
                identifiers.set(
                    key,
                    formatMetricLevelInstanceLabel(d.metric_identifier_type, d.metric_identifier_name)
                );
            }
        });
        return Array.from(identifiers.entries())
            .sort((a, b) => a[1].localeCompare(b[1]))
            .map(([key]) => key);
    };

    const getIdentifierLabel = (metricName: string, key: string): string => {
        if (!trendingData?.metrics_by_name?.[metricName]?.data) return key;
        const match = trendingData.metrics_by_name[metricName].data.find(
            (d) => metricLevelSeriesKey(d.metric_identifier_type, d.metric_identifier_name) === key
        );
        if (!match) return key;
        return formatMetricLevelInstanceLabel(
            match.metric_identifier_type,
            match.metric_identifier_name
        );
    };

    // Handle identifier filter change
    const handleIdentifierFilterChange = (metricName: string, newSelection: string[]) => {
        setSelectedIdentifiers(prev => ({
            ...prev,
            [metricName]: newSelection
        }));
    };

    // Get current metric chart option
    const currentMetricChartOption = selectedMetrics.length > 0 && activeMetricTab < selectedMetrics.length
        ? getSingleMetricChartOption(selectedMetrics[activeMetricTab])
        : null;

    return (
        <>
            {reviewEntityId && applicableDimensions.length > 0 && (
                        <Paper
                            elevation={0}
                            sx={{
                                mb: 3,
                                bgcolor: 'grey.50',
                                borderRadius: 2,
                                border: '1px solid',
                                borderColor: 'grey.200',
                                overflow: 'hidden'
                            }}
                        >
                            <Box sx={{ px: 2, pt: 2, pb: 0 }}>
                                <Typography variant="subtitle2" fontWeight={600} color="text.secondary">
                                    Review dimension
                                </Typography>
                            </Box>
                            <Tabs
                                value={activeDimensionName || sortedDimensionNames[0] || false}
                                onChange={(_e, value: string) => setActiveDimensionName(value)}
                                variant="scrollable"
                                scrollButtons="auto"
                                sx={{
                                    minHeight: 48,
                                    '& .MuiTab-root': { textTransform: 'none', minHeight: 48 }
                                }}
                            >
                                {sortedDimensionNames.map((dim) => {
                                    const dr = dimensionResults[dim];
                                    let statusIcon: React.ReactNode = null;
                                    if (!dr || dr.status === 'loading') {
                                        statusIcon = (
                                            <HourglassEmptyIcon sx={{ fontSize: 18 }} color="action" />
                                        );
                                    } else if (dr.status === 'error') {
                                        statusIcon = (
                                            <ErrorOutlineIcon sx={{ fontSize: 18 }} color="error" />
                                        );
                                    } else {
                                        const n = dr.data?.metrics_by_name
                                            ? Object.keys(dr.data.metrics_by_name).length
                                            : 0;
                                        if (n === 0) {
                                            statusIcon = (
                                                <InboxOutlinedIcon sx={{ fontSize: 18 }} color="disabled" />
                                            );
                                        }
                                    }
                                    return (
                                        <Tab
                                            key={dim}
                                            value={dim}
                                            label={
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 0.75,
                                                        maxWidth: 280
                                                    }}
                                                >
                                                    <span
                                                        style={{
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap'
                                                        }}
                                                    >
                                                        {dim}
                                                    </span>
                                                    {statusIcon}
                                                </Box>
                                            }
                                        />
                                    );
                                })}
                            </Tabs>
                        </Paper>
                    )}

                    {/* Navigation Breadcrumbs */}
                    {showEntityNavigation && navigationPath.length > 1 && (
                        <Box sx={{ mb: 3 }}>
                            <Breadcrumbs separator={<KeyboardArrowRightIcon fontSize="small" />}>
                                {navigationPath.map((item, index) => (
                                    index < navigationPath.length - 1 ? (
                                        <Link
                                            key={item.id}
                                            component="button"
                                            variant="body2"
                                            onClick={() => handleBreadcrumbClick(index)}
                                            sx={{ cursor: 'pointer', textDecoration: 'none' }}
                                        >
                                            {item.name}
                                        </Link>
                                    ) : (
                                        <Typography key={item.id} color="text.primary" variant="body2">
                                            {item.name}
                                        </Typography>
                                    )
                                ))}
                            </Breadcrumbs>
                        </Box>
                    )}

                    {/* View Mode Toggle and Chart Type Toggle */}
                    {trendingData && availableMetrics.length > 0 && (
                        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                            {/* View Mode Toggle - Only show for aggregate entities */}
                            {trendingData.is_aggregate && trendingData.hierarchy_info && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Typography variant="subtitle2" fontWeight={600}>
                                        View Mode:
                                    </Typography>
                                    <ToggleButtonGroup
                                        value={viewMode}
                                        exclusive
                                        onChange={handleViewModeChange}
                                        size="small"
                                    >
                                        <ToggleButton value="aggregate" sx={{ textTransform: 'none' }}>
                                            <FunctionsIcon sx={{ mr: 1, fontSize: 18 }} />
                                            Aggregated
                                        </ToggleButton>
                                        <ToggleButton value="compare" sx={{ textTransform: 'none' }}>
                                            <CompareArrowsIcon sx={{ mr: 1, fontSize: 18 }} />
                                            Compare Children
                                        </ToggleButton>
                                    </ToggleButtonGroup>
                                </Box>
                            )}
                            
                            {/* Chart Type Toggle */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Typography variant="subtitle2" fontWeight={600}>
                                    Chart Type:
                                </Typography>
                                <ToggleButtonGroup
                                    value={chartType}
                                    exclusive
                                    onChange={handleChartTypeChange}
                                    size="small"
                                >
                                    <ToggleButton value="line" sx={{ textTransform: 'none' }}>
                                        <TrendingUpIcon sx={{ mr: 1, fontSize: 18 }} />
                                        Line
                                    </ToggleButton>
                                    <ToggleButton value="bar" sx={{ textTransform: 'none' }}>
                                        <FilterListIcon sx={{ mr: 1, fontSize: 18, transform: 'rotate(90deg)' }} />
                                        Bar
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            </Box>
                        </Box>
                    )}

                    {/* Metric level select / filter / drill-down */}
                    {trendingData && availableMetrics.length > 0 && viewMode === 'aggregate' && (
                        <MetricLevelScopeControls
                            availableLevelTypes={availableLevelTypes}
                            selectedLevelType={selectedLevelType}
                            selectedInstanceId={selectedInstanceId}
                            metricLevelConfig={metricLevelConfig}
                            onLevelTypeChange={handleLevelTypeChange}
                            onInstanceChange={handleInstanceChange}
                            onBreadcrumbNavigate={handleLevelBreadcrumbNavigate}
                        />
                    )}

                    {/* Aggregate Metrics Indicator */}
                    {trendingData &&
                        availableMetrics.length > 0 &&
                        trendingData.is_aggregate &&
                        trendingData.hierarchy_info &&
                        viewMode === 'aggregate' && (
                        <Alert 
                            severity="info" 
                            icon={<FunctionsIcon />}
                            sx={{ mb: 3 }}
                        >
                            <Typography variant="subtitle2" fontWeight={600}>
                                Aggregated Metrics View
                            </Typography>
                            <Typography variant="body2">
                                This entity has {trendingData.hierarchy_info.direct_children_count} direct children 
                                and {trendingData.hierarchy_info.leaf_count} leaf entities. 
                                Metrics shown are averages computed from all leaf entities in the hierarchy.
                            </Typography>
                        </Alert>
                    )}

                    {/* Drill-Down Section - Only show in aggregate mode */}
                    {showEntityNavigation &&
                        trendingData &&
                        trendingData.hierarchy_info &&
                        trendingData.hierarchy_info.direct_children.length > 0 &&
                        viewMode === 'aggregate' && (
                        <Paper
                            elevation={0}
                            sx={{
                                p: 2,
                                mb: 3,
                                bgcolor: 'primary.50',
                                borderRadius: 2,
                                border: '1px solid',
                                borderColor: 'primary.200'
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                <AccountTreeIcon sx={{ color: 'primary.main' }} />
                                <Typography variant="subtitle1" fontWeight={600} color="primary.main">
                                    Drill Down to Child Entities
                                </Typography>
                            </Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Click on a child entity to view its specific metrics or aggregated metrics (if it has children).
                            </Typography>
                            <List dense sx={{ bgcolor: 'background.paper', borderRadius: 1 }}>
                                {trendingData.hierarchy_info.direct_children.map((child, index) => (
                                    <React.Fragment key={child.id}>
                                        {index > 0 && <Divider />}
                                        <ListItem disablePadding>
                                            <ListItemButton onClick={() => handleDrillDown(child)}>
                                                <ListItemIcon sx={{ minWidth: 40 }}>
                                                    {/* Show folder icon if entity likely has children, file icon otherwise */}
                                                    <FolderIcon color="primary" />
                                                </ListItemIcon>
                                                <ListItemText 
                                                    primary={child.name}
                                                    secondary={child.review_entity_type || 'Entity'}
                                                />
                                                <KeyboardArrowRightIcon color="action" />
                                            </ListItemButton>
                                        </ListItem>
                                    </React.Fragment>
                                ))}
                            </List>
                        </Paper>
                    )}

                    {/* Comparison View Section */}
                    {viewMode === 'compare' && trendingData?.is_aggregate && (
                        <Box sx={{ mb: 3 }}>
                            {/* Comparison Metric Selector */}
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 2,
                                    mb: 3,
                                    bgcolor: 'secondary.50',
                                    borderRadius: 2,
                                    border: '1px solid',
                                    borderColor: 'secondary.200'
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                    <CompareArrowsIcon sx={{ color: 'secondary.main' }} />
                                    <Typography variant="subtitle1" fontWeight={600} color="secondary.main">
                                        Compare Children Metrics
                                    </Typography>
                                </Box>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    Select a metric to compare across all {trendingData.hierarchy_info?.direct_children_count || 0} child entities.
                                </Typography>
                                
                                <Autocomplete
                                    options={
                                        comparisonData && Object.keys(comparisonData.comparison_by_metric).length > 0 
                                            ? Object.keys(comparisonData.comparison_by_metric)
                                            : comparisonData?.available_metrics || []
                                    }
                                    value={selectedCompareMetric || null}
                                    onChange={(event, newValue) => {
                                        setSelectedCompareMetric(newValue || '');
                                    }}
                                    disabled={loadingComparison}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Select Metric to Compare"
                                            placeholder="Search metrics..."
                                            size="small"
                                        />
                                    )}
                                    fullWidth
                                />
                                {loadingComparison && (
                                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                                        Loading metrics from child entities...
                                    </Typography>
                                )}
                            </Paper>

                            {/* Comparison Chart */}
                            {loadingComparison ? (
                                <Box sx={{ p: 6, textAlign: 'center' }}>
                                    <CircularProgress />
                                    <Typography variant="body1" sx={{ mt: 2 }}>
                                        Loading comparison data...
                                    </Typography>
                                </Box>
                            ) : comparisonChartOption ? (
                                <Box
                                    sx={{
                                        bgcolor: '#fafafa',
                                        borderRadius: 2,
                                        p: 2,
                                        border: '1px solid',
                                        borderColor: 'grey.100'
                                    }}
                                >
                                    <ReactECharts
                                        option={comparisonChartOption}
                                        style={{ height: '500px', width: '100%' }}
                                        opts={{ renderer: 'svg' }}
                                        notMerge={true}
                                    />
                                </Box>
                            ) : selectedCompareMetric && comparisonData ? (
                                <Box sx={{ p: 4, textAlign: 'center', bgcolor: 'warning.50', borderRadius: 2, border: '1px solid', borderColor: 'warning.200' }}>
                                    <CompareArrowsIcon sx={{ fontSize: 48, color: 'warning.main', mb: 2 }} />
                                    <Typography variant="body1" color="text.secondary">
                                        No data available for metric "{selectedCompareMetric}" across child entities.
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                        Available metrics: {comparisonData.available_metrics.length > 0 
                                            ? comparisonData.available_metrics.join(', ') 
                                            : 'None found in child entities'}
                                    </Typography>
                                </Box>
                            ) : (
                                <Box sx={{ p: 4, textAlign: 'center', bgcolor: 'grey.50', borderRadius: 2 }}>
                                    <CompareArrowsIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                                    <Typography variant="body1" color="text.secondary">
                                        Select a metric above to compare across child entities
                                    </Typography>
                                </Box>
                            )}

                            {/* Comparison Summary */}
                            {comparisonData && comparisonData.children_count > 0 && (
                                <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                                    <Typography variant="subtitle2" color="text.secondary">
                                        Comparing {comparisonData.children_count} child entities
                                        {comparisonData.available_metrics.length > 0 && (
                                            <> • {comparisonData.available_metrics.length} metrics available</>
                                        )}
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    )}

                    {/* Metric Selection - Only show in aggregate mode */}
                    {availableMetrics.length > 0 && viewMode === 'aggregate' && (
                        <Box sx={{ mb: 3 }}>
                            <Autocomplete
                                multiple
                                options={availableMetrics}
                                value={selectedMetrics}
                                onChange={(event, newValue) => {
                                    setSelectedMetrics(newValue);
                                }}
                                disableCloseOnSelect
                                getOptionLabel={(option) => option}
                                renderOption={(props, option, { selected }) => (
                                    <li {...props}>
                                        <Checkbox
                                            icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                                            checkedIcon={<CheckBoxIcon fontSize="small" />}
                                            style={{ marginRight: 8 }}
                                            checked={selected}
                                        />
                                        {option}
                                    </li>
                                )}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Select Metrics to Display"
                                        placeholder="Search metrics..."
                                    />
                                )}
                                renderTags={(value, getTagProps) =>
                                    value.map((option, index) => (
                                        <Chip
                                            {...getTagProps({ index })}
                                            key={option}
                                            label={option}
                                            size="small"
                                            color="primary"
                                        />
                                    ))
                                }
                                fullWidth
                                limitTags={5}
                            />
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                {selectedMetrics.length} of {availableMetrics.length} metrics selected
                            </Typography>
                        </Box>
                    )}

                    {/* Chart Content - Only show in aggregate mode or for non-aggregate entities */}
                    {(viewMode === 'aggregate' || !trendingData?.is_aggregate) && (
                        <>
                            {!reviewEntityId && showSelectEntityPrompt ? (
                                <Box sx={{ p: 6, textAlign: 'center', bgcolor: 'grey.50', borderRadius: 2 }}>
                                    <TrendingUpIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                                    <Typography variant="h6" color="text.secondary">
                                        Select a Review Entity to view metrics trending
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                        Only entities where you are a reviewer or reviewee are shown
                                    </Typography>
                                </Box>
                            ) : !reviewEntityId ? null : loadingApplicableDimensions ? (
                                <Box sx={{ p: 6, textAlign: 'center' }}>
                                    <CircularProgress />
                                    <Typography variant="body1" sx={{ mt: 2 }}>
                                        Loading review dimensions...
                                    </Typography>
                                </Box>
                            ) : error && applicableDimensions.length === 0 ? (
                                <Alert severity="error" sx={{ mb: 3 }}>
                                    {error}
                                </Alert>
                            ) : applicableDimensions.length === 0 ? (
                                <Box sx={{ p: 6, textAlign: 'center', bgcolor: 'grey.50', borderRadius: 2 }}>
                                    <Typography variant="h6" color="text.secondary">
                                        No review dimensions apply to this entity
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                        Dimensions are configured per entity type or entity attributes.
                                    </Typography>
                                </Box>
                            ) : !activeDimensionState || activeDimensionState.status === 'loading' ? (
                                <Box sx={{ p: 6, textAlign: 'center' }}>
                                    <CircularProgress />
                                    <Typography variant="body1" sx={{ mt: 2 }}>
                                        Loading metrics
                                        {activeDimensionName ? ` (${activeDimensionName})` : ''}...
                                    </Typography>
                                </Box>
                            ) : activeDimensionState.status === 'error' ? (
                                <Alert severity="error" sx={{ mb: 3 }}>
                                    {activeDimensionState.error}
                                </Alert>
                            ) : activeDimensionState.status === 'ok' &&
                              (!trendingData ||
                                  Object.keys(trendingData.metrics_by_name || {}).length === 0) ? (
                                <Box sx={{ p: 6, textAlign: 'center', bgcolor: 'grey.50', borderRadius: 2 }}>
                                    <InboxOutlinedIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 2 }} />
                                    <Typography variant="h6" color="text.secondary">
                                        No metrics for this dimension
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                        Try another dimension tab or check if metrics have been computed.
                                    </Typography>
                                </Box>
                            ) : error ? (
                                <Alert severity="error" sx={{ mb: 3 }}>
                                    {error}
                                </Alert>
                            ) : selectedMetrics.length === 0 ? (
                                <Box sx={{ p: 6, textAlign: 'center', bgcolor: 'grey.50', borderRadius: 2 }}>
                                    <Typography variant="h6" color="text.secondary">
                                        Select at least one metric to display the chart
                                    </Typography>
                                </Box>
                            ) : (
                                <Paper 
                                    elevation={0} 
                                    sx={{ 
                                        bgcolor: '#fafafa', 
                                        borderRadius: 2, 
                                        border: '1px solid', 
                                        borderColor: 'grey.200',
                                        overflow: 'hidden'
                                    }}
                                >
                                    {/* Metric Tabs */}
                                    <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
                                        <Tabs 
                                            value={activeMetricTab} 
                                            onChange={handleMetricTabChange}
                                            variant="scrollable"
                                            scrollButtons="auto"
                                            sx={{
                                                '& .MuiTab-root': {
                                                    textTransform: 'none',
                                                    minHeight: 48,
                                                    fontWeight: 500
                                                }
                                            }}
                                        >
                                            {selectedMetrics.map((metricName, index) => {
                                                const metricData = trendingData?.metrics_by_name[metricName];
                                                const valueType = (metricData as any)?.value_type;
                                                return (
                                                    <Tab 
                                                        key={metricName}
                                                        label={
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <span>{metricName}</span>
                                                                {valueType && (
                                                                    <Chip 
                                                                        label={valueType} 
                                                                        size="small" 
                                                                        variant="outlined"
                                                                        sx={{ 
                                                                            height: 20, 
                                                                            fontSize: '0.7rem',
                                                                            '& .MuiChip-label': { px: 1 }
                                                                        }} 
                                                                    />
                                                                )}
                                                            </Box>
                                                        }
                                                    />
                                                );
                                            })}
                                        </Tabs>
                                    </Box>
                                    
                                    {/* Identifier Filter - show when multiple identifiers exist */}
                                    {selectedMetrics.length > 0 && activeMetricTab < selectedMetrics.length && (() => {
                                        const currentMetric = selectedMetrics[activeMetricTab];
                                        const identifiers = getIdentifierNamesForMetric(currentMetric);
                                        if (identifiers.length <= 1) return null;
                                        
                                        return (
                                            <Box sx={{ px: 2, pt: 2, pb: 1 }}>
                                                <Autocomplete
                                                    multiple
                                                    size="small"
                                                    options={identifiers}
                                                    value={selectedIdentifiers[currentMetric] || []}
                                                    onChange={(_, newValue) => handleIdentifierFilterChange(currentMetric, newValue)}
                                                    disableCloseOnSelect
                                                    getOptionLabel={(option) => getIdentifierLabel(currentMetric, option)}
                                                    renderOption={(props, option, { selected }) => (
                                                        <li {...props}>
                                                            <Checkbox
                                                                icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                                                                checkedIcon={<CheckBoxIcon fontSize="small" />}
                                                                style={{ marginRight: 8 }}
                                                                checked={selected}
                                                            />
                                                            {getIdentifierLabel(currentMetric, option)}
                                                        </li>
                                                    )}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            label="Filter by Metric Level"
                                                            placeholder={selectedIdentifiers[currentMetric]?.length ? '' : 'All levels shown'}
                                                            helperText={`${identifiers.length} instance${identifiers.length === 1 ? '' : 's'} at this level. Select to narrow further.`}
                                                        />
                                                    )}
                                                    sx={{ maxWidth: 500 }}
                                                />
                                            </Box>
                                        );
                                    })()}
                                    
                                    {/* Chart for Active Tab */}
                                    <Box sx={{ p: 2 }}>
                                        {currentMetricChartOption ? (
                                            <ReactECharts
                                                option={currentMetricChartOption}
                                                style={{ height: '450px', width: '100%' }}
                                                opts={{ renderer: 'svg' }}
                                                notMerge={true}
                                            />
                                        ) : (
                                            <Box sx={{ p: 4, textAlign: 'center' }}>
                                                <Typography color="text.secondary">
                                                    No data at the selected metric level
                                                    {selectedInstanceId ? ' / instance' : ''}. Choose
                                                    another level or drill-down target.
                                                </Typography>
                                            </Box>
                                        )}
                                    </Box>
                                </Paper>
                            )}

                            {/* Data Summary */}
                            {trendingData && trendingData.data_points && trendingData.data_points.length > 0 && (
                                <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                                    <Typography variant="subtitle2" color="text.secondary">
                                        Data Summary: {trendingData.data_points.length} data points across{' '}
                                        {Object.keys(trendingData.metrics_by_name || {}).length} metrics
                                        {trendingData.is_aggregate && trendingData.hierarchy_info && (
                                            <> • Aggregated from {trendingData.hierarchy_info.leaf_count} leaf entities</>
                                        )}
                                    </Typography>
                                </Box>
                            )}
                        </>
                    )}
        </>
    );
};

export default ReviewEntityMetricsTrending;
